<script setup lang="ts">
  import { computed, inject, onBeforeUnmount, onMounted, provide, ref, watch, watchEffect } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import { Form, Input, InputNumber, Select, SelectOption, Checkbox, Button } from 'ant-design-vue'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { CustomReport } from '../models/CustomReport'
  import { CustomReportSchema } from '../models/CustomReportSchema'
  import { CustomReportFieldType } from '../models/CustomReportFieldType'
  import { debounceTime, groupBy, map, mergeMap, Subject } from 'rxjs'
  import { parseSmartValue, DEFAULT_INPUT_DEBOUNCE_MS } from '@/utils'
  import { CustomReportsService } from '../services/CustomReportsService'
  import { MinusSquare, PlusSquare } from 'lucide-vue-next'
  import {
    AppstoreOutlined,
    BorderOutlined,
    CompressOutlined,
    ExpandOutlined
  } from '@ant-design/icons-vue'
  import ArrayValuesCustomReportConfiguration from './ArrayValuesCustomReportConfiguration.vue'
  import JsonCustomReportConfiguration from './JsonCustomReportConfiguration.vue'
  import SelectOrCreateCustomReportConfiguration from './SelectOrCreateCustomReportConfiguration.vue'
  import { CustomReportField } from '../models/CustomReportField'
  import ColorCustomReportConfiguration from './ColorCustomReportConfiguration.vue'
  import { CustomReportSchemaUiFieldGroup } from '../models/CustomReportSchemaUi'
  import ArrayCustomReportBuilder from './ArrayCustomReportBuilder.vue'
  import ObjectCustomReportBuilder from './ObjectCustomReportBuilder.vue'
  import RegexCustomReportConfiguration from './RegexCustomReportConfiguration.vue'
  import CustomReportHelpPopover from '../help/components/CustomReportHelpPopover.vue'
  import { CustomReportJsonReportConfigurationService } from '../services/CustomReportJsonReportConfigurationService'
  import { PostProcessingOutput } from '@/models/PostProcessingOutput'
  import { PostProcessingOutputPlot } from '@/models/PostProcessingOutputPlot'
  import { PostProcessingOutputTable } from '@/models/PostProcessingOutputTable'
import CustomReportSelectMappingConfiguration from './CustomReportSelectMappingConfiguration.vue'
  import { CustomReportsUiService } from '../services/CustomReportsUiService'

  interface ICustomReportConfigurationProps {
    customReportSchemas: Array<CustomReportSchema>
    customReport: CustomReport | null
    customPlotsOutput?: PostProcessingOutput<PostProcessingOutputPlot>
    customTablesOutput?: PostProcessingOutput<PostProcessingOutputTable>
    uiFieldGroup?: CustomReportSchemaUiFieldGroup
    configurationLevel: number
    showDescription?: boolean
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    currentPath?: string[]
  }

  interface ICustomReportConfigurationEmits {
    (
      e: 'configuration-updated',
      customReport: CustomReport,
      config: Record<string, any>,
      customReportTypeChange?: boolean
    ): void
  }

  const props = defineProps<ICustomReportConfigurationProps>()
  const emit = defineEmits<ICustomReportConfigurationEmits>()

  const configuration = ref<Record<string, any>>({})
  const isCollapsed = ref<boolean>(false)
  const isCondensedLayout = ref<boolean>(props.isCondensedLayout)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })
  const { controlSize } = useWindowSize()

  if (props.configurationLevel === 1) {
    provide('rootConfiguration', configuration)
  }

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const customReportDescription = ref(props.customReport?.configuration?.description ?? '')

  watch(customReportDescription, (newVal) => {
    if (configuration.value) {
      configuration.value.description = newVal
      handleConfigurationChange()
    }
  })

  const inputValueChanged$ = new Subject<{ field: string; value: string }>()

  const inputValueChangedSubscription = inputValueChanged$
    .pipe(
      groupBy(({ field }) => field),
      mergeMap((group$) =>
        group$.pipe(
          debounceTime(DEFAULT_INPUT_DEBOUNCE_MS),
          map(({ field, value }) => ({ field, value }))
        )
      )
    )
    .subscribe(({ field, value }) => {
      const parsedValue = parseSmartValue(value)
      configuration.value[field] = parsedValue
      handleConfigurationChange()
    })

  // Initialize configuration with default values
  const initializeConfiguration = () => {
    if (!props.customReport) return
    configuration.value = CustomReportsService.initializeCustomReportConfiguration(
      props.customReport,
      props.customReport.schema.fields
    )
  }

  const handleConfigurationChange = () => {
    if (props.customReport) {
      emit('configuration-updated', props.customReport, configuration.value)
    }
  }

  const handleChildCustomReportTypeChanged = (
    customReportType: string,
    childCustomReport: CustomReport,
    configuration: any
  ) => {
    if (childCustomReport) {
      emit(
        'configuration-updated',
        { ...childCustomReport, _type: customReportType },
        configuration,
        true
      )
    }
  }

  const handleCustomReportTypeChanged = (customReportType: string, configuration: any) => {
    if (props.customReport) {
      emit(
        'configuration-updated',
        { ...props.customReport, _type: customReportType },
        configuration?.value,
        true
      )
    }
  }

  watch(
    () => props.customReport,
    (newCustomReport) => {
      if (newCustomReport) {
        configuration.value = { ...newCustomReport.configuration }
        // initialize with defaults
        if (Object.keys(configuration.value).length === 0) {
          initializeConfiguration()
          handleConfigurationChange()
        }
        customReportDescription.value = configuration.value.description ?? ''
      }
    },
    { immediate: true, deep: true }
  )

  const pendingRefocus = inject(pendingRefocusKey)

  let isUnmounting = false

  const onInputChange = (newValue: string, fieldName: string) => {
    const activeEl = document.activeElement as HTMLInputElement | null
    if (pendingRefocus) {
      pendingRefocus.value = {
        levelFieldKey: `${props.configurationLevel}:${fieldName}`,
        cursor: activeEl?.selectionStart ?? null
      }
    }
    inputValueChanged$.next({ value: newValue, field: fieldName })
  }

  const onInputBlur = (fieldKey: string) => {
    if (isUnmounting) return
    if (pendingRefocus?.value?.levelFieldKey === `${props.configurationLevel}:${fieldKey}`) {
      pendingRefocus.value = null
    }
  }

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value
  }

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  const onLayoutModeChanged = (e: any) => {
    isCondensedLayout.value = e.target.value
  }

  // If only single field, do not use grid css
  const simpleFieldCount = computed(() =>
    props.customReport
      ? props.customReport.schema.fields.filter(
          (f: any) =>
            f.field_type !== CustomReportFieldType.Object &&
            f.field_type !== CustomReportFieldType.Array &&
            f.field_type !== CustomReportFieldType.Json
        ).length
      : 0
  )

  const configuredFields = computed(() => {
    const customReport = props.customReport
    const uiFieldGroup = props.uiFieldGroup

    if (!customReport) {
      return []
    }

    const allFields = customReport.schema.fields

    if (!uiFieldGroup || !Array.isArray(uiFieldGroup.fields) || uiFieldGroup.fields.length === 0) {
      return allFields
    }

    // Ensure order matches uiFieldGroup.fields
    return uiFieldGroup.fields
      .map((fieldName) => allFields.find((field: CustomReportField) => field.name === fieldName))
      .filter((field): field is CustomReportField => !!field)
  })

  const shouldShowGlobalCollapseButton = computed(() => {
    return (
      props.customReport?.schema?.fields?.findIndex(
        (x: CustomReportField) =>
          x.field_type === CustomReportFieldType.Array ||
          x.field_type === CustomReportFieldType.Object ||
          x.field_type === CustomReportFieldType.Json
      ) !== -1
    )
  })

  const shouldShowFieldInformation = (field: CustomReportField): boolean => {
    return (
      field.field_type !== CustomReportFieldType.Object &&
      field.field_type !== CustomReportFieldType.Json
    )
  }

  const checkboxSizeClass = computed(() => {
    if (controlSize.value === 'small') return 'checkbox-sm'
    if (controlSize.value === 'large') return 'checkbox-lg'
    return 'checkbox-md'
  })

  const displayField = computed(() => {
    const mappedField = props.customReport?.display_field as
      | string
      | Array<string>
      | null
      | undefined
    if (mappedField == null) return undefined

    const fields = props.customReport?.schema?.fields ?? []

    const valueForName = (name: string) => {
      const matchingField = fields.find((x: any) => x.name === name)
      if (!matchingField) return undefined
      const val = configuration.value?.[matchingField.name]
      return val === undefined ? undefined : val
    }

    if (typeof mappedField === 'string') {
      return valueForName(mappedField)
    }

    if (Array.isArray(mappedField)) {
      for (const name of mappedField) {
        const v = valueForName(name)
        if (v) return v
      }
    }

    return undefined
  })

  onMounted(() => {
    if (pendingRefocus?.value) {
      const { levelFieldKey, cursor } = pendingRefocus.value
      let el = document.querySelector(`[data-field="${levelFieldKey}"]`) as HTMLElement | null
      if (el && el.tagName !== 'INPUT') el = el.querySelector('input') ?? el
      const inputEl = el as HTMLInputElement | null
      if (inputEl?.tagName === 'INPUT') {
        inputEl.focus()
        if (cursor !== null) {
          try { inputEl.setSelectionRange(cursor, cursor) } catch {}
        }
      }
      pendingRefocus.value = null
    }
  })

  onBeforeUnmount(() => {
    isUnmounting = true
    inputValueChangedSubscription?.unsubscribe()
  })
</script>

<template>
  <div
    v-if="customReport"
    :class="[
      'border',
      'customReport-configuration',
      'h-full',
      'w-full',
      {
        '!bg-[var(--card)] dark:!bg-[var(--card-light)]': props.configurationLevel % 2 !== 0,
        '!bg-[var(--card-light)] dark:!bg-[var(--card)]': props.configurationLevel % 2 === 0,
        'child-customReport-configuration border p-2': props.configurationLevel > 1
      }
    ]"
  >
    <div class="flex h-full flex-col">
      <div v-if="props.configurationLevel <= 1" class="mb-2 flex items-center justify-end gap-2">
        <a-tooltip>
          <template #title>Toggle layout mode</template>
          <a-radio-group
            :value="isCondensedLayout"
            @change="onLayoutModeChanged"
            :size="controlSize"
          >
            <a-radio-button :value="true">
              <AppstoreOutlined class="radio-icon-center" />
            </a-radio-button>
            <a-radio-button :value="false">
              <BorderOutlined class="radio-icon-center" />
            </a-radio-button>
          </a-radio-group>
        </a-tooltip>
      </div>
      <div class="horizontal-rule mb-4 border-b pb-1" v-if="props.configurationLevel > 1">
        <div class="flex">
          <div>
            <h3 class="mt-1 text-lg font-semibold">
              Configure: {{ customReport.display_name }}
              <span v-if="displayField != null">{{ `(${displayField})` }}</span>
            </h3>
          </div>
          <div class="ml-auto flex">
            <a-tooltip placement="top">
              <template #title>Toggle Collapse</template>
              <Button
                style="padding: 3px; border: none"
                type="text"
                :size="controlSize"
                class="flex items-center justify-center"
                @click="toggleCollapse"
                aria-label="Toggle Collapse"
              >
                <PlusSquare
                  v-if="isCollapsed"
                  class="text-base"
                  style="line-height: 1; margin: 0"
                />
                <MinusSquare v-else class="text-base" style="line-height: 1; margin: 0" />
              </Button>
            </a-tooltip>

            <a-tooltip v-if="shouldShowGlobalCollapseButton" placement="top">
              <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
              <Button
                style="padding: 3px; border: none"
                type="text"
                :size="controlSize"
                class="flex items-center justify-center"
                @click="toggleGlobalCollapse"
                aria-label="Toggle Collapse All"
              >
                <ExpandOutlined
                  v-if="isCollapsed"
                  class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                  style="line-height: 1; margin: 0"
                />
                <CompressOutlined
                  v-else
                  class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                  style="line-height: 1; margin: 0"
                />
              </Button>
            </a-tooltip>
          </div>
        </div>
      </div>
      <div
        v-else-if="
          customReport._type !== CustomReportsService.CUSTOM_CUSTOMREPORT_TYPE &&
          props.showDescription === true
        "
        class="mb-4"
      >
        <label class="mb-1 block text-sm font-medium">Description</label>
        <Input
          v-model:value="customReportDescription"
          @change="handleConfigurationChange"
          placeholder="Custom report description"
          :size="controlSize"
        />
      </div>

      <div
        :class="[
          'flex-1',
          'overflow-y-auto',
          { 'collapsed-customReport-configuration': isCollapsed }
        ]"
      >
        <Form ref="formRef" :model="configuration" layout="vertical">
          <div
            :class="{
              'customReport-form-fields-grid': true,
              expanded:
                props.configurationLevel === 1 ? !isCondensedLayout : !props.isCondensedLayout
            }"
          >
            <div
              v-for="configuredField in configuredFields"
              :key="configuredField.name"
              class="customReport-form-field-grid"
              :class="{
                'array-customReport-builder-grid-span':
                  configuredField.field_type === CustomReportFieldType.Array,
                'object-customReport-builder-grid-span':
                  configuredField.field_type === CustomReportFieldType.Object,
                'json-customReport-configuration-grid-span':
                  configuredField.field_type === CustomReportFieldType.Json,
                'single-field-grid-span':
                  simpleFieldCount <= 1 &&
                  configuredField.field_type !== CustomReportFieldType.Object &&
                  configuredField.field_type !== CustomReportFieldType.Array &&
                  configuredField.field_type !== CustomReportFieldType.Json,
                '!hidden': !CustomReportsUiService.isFieldVisibleFromRoot(
                  configuredField,
                  props.customReport,
                  props.currentPath
                    ? [...props.currentPath, configuredField.name]
                    : [configuredField.name]
                )
              }"
            >
              <div class="flex">
                <div v-if="shouldShowFieldInformation(configuredField)" class="min-w-0 flex-1">
                  <label class="customReport-field-label mb-1 block text-sm font-medium">
                    {{ configuredField.display_name }}
                    <span v-if="configuredField.required" class="text-red-500">*</span>
                  </label>
                  <p
                    v-if="configuredField.description"
                    class="customReport-field-label mb-2 text-xs"
                  >
                    {{ configuredField.description }}
                  </p>
                </div>
                <div
                  v-if="
                    configuredField.help_content != null && configuredField.help_content?.length > 0
                  "
                  class="m1-auto flex justify-end"
                >
                  <CustomReportHelpPopover
                    :content-type="configuredField.help_content"
                    :title="`${configuredField.display_name} Help`"
                  />
                </div>
              </div>

              <!-- Text Input -->
              <Input
                v-if="configuredField.field_type === CustomReportFieldType.Text"
                :value="configuration[configuredField.name]"
                @update:value="(val: string) => onInputChange(val, configuredField.name)"
                @blur="() => onInputBlur(configuredField.name)"
                :data-field="`${props.configurationLevel}:${configuredField.name}`"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                :placeholder="configuredField.placeholder || ''"
                :size="controlSize"
              />

              <!-- Number Input -->
              <InputNumber
                v-else-if="configuredField.field_type === CustomReportFieldType.Number"
                v-model:value="configuration[configuredField.name]"
                :placeholder="configuredField.placeholder || ''"
                :size="controlSize"
                @change="handleConfigurationChange"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                class="w-full"
              />

              <!-- Regex Input -->
              <RegexCustomReportConfiguration
                v-else-if="configuredField.field_type === CustomReportFieldType.RegularExpression"
                v-model="configuration[configuredField.name]"
                :required="configuredField.required"
                :regex="configuredField.field_type_args?.regex_pattern"
                :data-type="configuredField.field_type_args?.regex_data_type"
                :placeholder="configuredField.placeholder || ''"
                :error-message="configuredField.field_type_args?.regex_description"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Select Dropdown -->
              <Select
                v-else-if="configuredField.field_type === CustomReportFieldType.Select"
                v-model:value="configuration[configuredField.name]"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="option in configuredField.options"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>
              </Select>

              <!-- Select Dropdown JSON Payload -->
              <Select
                v-else-if="configuredField.field_type === CustomReportFieldType.SelectJson"
                :value="JSON.stringify(configuration[configuredField.name])"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @update:value="
                  (val: string) => {
                    try {
                      const parsed = JSON.parse(val)
                      configuration[configuredField.name] = parsed
                      handleConfigurationChange()
                    } catch (e) {
                      console.error('Failed to parse selected JSON value', e)
                    }
                  }
                "
                :size="controlSize"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="option in configuredField.options"
                  :key="JSON.stringify(option.value)"
                  :value="JSON.stringify(option.value)"
                >
                  <span v-html="option.label"></span>
                </SelectOption>
              </Select>

              <!-- Select Dropdown Or Create -->
              <SelectOrCreateCustomReportConfiguration
                v-else-if="configuredField.field_type === CustomReportFieldType.SelectOrCreate"
                v-model="configuration[configuredField.name]"
                :placeholder="`Select or enter new ${configuredField.display_name.toLowerCase()}`"
                :options="configuredField.options"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Multi-Select -->
              <Select
                v-else-if="configuredField.field_type === CustomReportFieldType.MultiSelect"
                v-model:value="configuration[configuredField.name]"
                mode="multiple"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-if="configuredField.field_type === CustomReportFieldType.MultiSelect"
                  v-for="option in configuredField.options"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>
              </Select>

              <!-- Boolean Checkbox -->
              <Checkbox
                v-else-if="configuredField.field_type === CustomReportFieldType.Boolean"
                @change="handleConfigurationChange"
                v-model:checked="configuration[configuredField.name]"
                :disabled="configuredField.readonly"
                :readonly="configuredField.readonly"
                :class="checkboxSizeClass"
              >
                {{ configuredField.display_name }}
              </Checkbox>

              <!-- Array Values -->
              <ArrayValuesCustomReportConfiguration
                v-else-if="configuredField.field_type === CustomReportFieldType.ArrayValues"
                v-model="configuration[configuredField.name]"
                :place-holder="configuredField.placeholder || 'Enter value(s)'"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Color Picker -->
              <ColorCustomReportConfiguration
                v-else-if="configuredField.field_type === CustomReportFieldType.ColorPicker"
                v-model="configuration[configuredField.name]"
                :place-holder="configuredField.placeholder || 'Select color'"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Array Type -->
              <ArrayCustomReportBuilder
                v-else-if="configuredField.field_type === CustomReportFieldType.Array"
                :configuration-level="props.configurationLevel"
                v-model="configuration[configuredField.name]"
                :custom-report-field="configuredField"
                :custom-plots-output="customPlotsOutput"
                :custom-tables-output="customTablesOutput"
                :key="customReport.id"
                @update:modelValue="handleConfigurationChange"
                @child-customReport-type-changed="handleChildCustomReportTypeChanged"
                :customReportSchemas="customReportSchemas"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
              />

              <!-- Select/MultiSelect Mapping -->
              <CustomReportSelectMappingConfiguration
                v-else-if="
                  configuredField.field_type === CustomReportFieldType.SelectMapping ||
                  configuredField.field_type === CustomReportFieldType.MultiSelectMapping
                "
                v-model="configuration[configuredField.name]"
                :field="configuredField"
                :placeholder="configuredField.placeholder"
                :local-configuration="configuration"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Object Type -->
              <ObjectCustomReportBuilder
                v-else-if="configuredField.field_type === CustomReportFieldType.Object"
                :configuration-level="props.configurationLevel + 1"
                v-model="configuration[configuredField.name]"
                :custom-report-field="configuredField"
                :custom-plots-output="customPlotsOutput"
                :custom-tables-output="customTablesOutput"
                @update:modelValue="handleConfigurationChange"
                :customReportSchemas="customReportSchemas"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
                :root-custom-report="props.customReport"
                :current-path="
                  props.currentPath
                    ? [...props.currentPath, configuredField.name]
                    : [configuredField.name]
                "
              />

              <!-- Json Type (Custom Config) -->
              <JsonCustomReportConfiguration
                v-else-if="configuredField.field_type === CustomReportFieldType.Json"
                :customReportSchemas="props.customReportSchemas"
                v-model="configuration"
                :editor-schema="
                  CustomReportJsonReportConfigurationService.getJsonEditorSchemaForCustomReport()
                "
                :place-holder="configuredField.placeholder || 'Configure Json'"
                :configuration-level="props.configurationLevel"
                @calculation-type-changed="handleCustomReportTypeChanged"
                @update:modelValue="handleConfigurationChange"
                :allow-type-change="true"
              />

              <!-- Default fallback for unknown field types -->
              <JsonCustomReportConfiguration
                v-else
                :customReportSchemas="props.customReportSchemas"
                v-model="configuration[configuredField.name]"
                :editor-schema="
                  CustomReportJsonReportConfigurationService.getJsonEditorSchemaForField(
                    configuredField
                  )
                "
                :place-holder="configuredField.placeholder || 'Configure Json'"
                :configuration-level="props.configurationLevel"
                @update:modelValue="handleConfigurationChange"
                :allow-type-change="false"
              />
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>

  <div v-else class="flex h-full items-center justify-center text-gray-500">
    <div class="mt-8 text-center">
      <p class="mb-2 text-lg">No Custom Report Selected</p>
      <p class="text-sm">Select a custom report from the left panel to configure its settings</p>
    </div>
  </div>
</template>

<style scoped>
  .customReport-configuration {
    padding: 10px;
  }

  .collapsed-customReport-configuration {
    display: none;
  }

  .horizontal-rule {
    border-bottom-color: #0000001f;
  }

  .customReport-form-fields-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: 1100px) {
    .customReport-form-fields-grid:not(.expanded) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .customReport-form-field-grid {
    width: 100%;
    min-height: 90px;
    max-height: 110px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: hidden;
  }

  .array-customReport-builder-grid-span,
  .object-customReport-builder-grid-span,
  .json-customReport-configuration-grid-span,
  .single-field-grid-span {
    grid-column: 1 / -1 !important;
    min-height: unset;
    max-height: unset;
    overflow: visible;
  }

  .customReport-field-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
  }

  .customReport-field-description {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
  }

  .radio-icon-center {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 18px;
  }
</style>
